import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useCssVars } from '@/hooks/use-css-vars';
import { X } from 'lucide-react';

interface ScanProgressProps {
  progress: number; // 0-100
  message: string;
  onCancel: () => void;
}

export function ScanProgress({ progress, message, onCancel }: ScanProgressProps) {
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const size = 180;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (animatedProgress / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedProgress(progress), 50);
    return () => clearTimeout(timer);
  }, [progress]);

  // Estimate remaining time based on progress
  const estimatedTotal = 10; // seconds for demo
  const elapsed = (progress / 100) * estimatedTotal;
  const remaining = Math.max(0, Math.ceil(estimatedTotal - elapsed));

  // Set ring size via CSS custom property so we can avoid an inline `style`
  // attribute (CSP style-src-attr is strict).
  const ringRef = useCssVars<HTMLDivElement>({ '--ring-size': `${size}px` });

  return (
    <div className="flex flex-col items-center gap-6 py-8">
      {/* Progress ring */}
      <div ref={ringRef} className="relative w-[var(--ring-size)] h-[var(--ring-size)]">
        <svg width={size} height={size} className="-rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            className="stroke-gray-200 dark:stroke-gray-700"
            stroke="currentColor"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#3b82f6"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-500 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-gray-900 dark:text-foreground">
            {Math.round(progress)}%
          </span>
          <span className="text-xs text-muted-foreground dark:text-muted-foreground font-medium">scanning</span>
        </div>
      </div>

      {/* Step message */}
      <p className="text-sm text-muted-foreground dark:text-muted-foreground text-center min-h-[1.25rem] transition-opacity duration-300">
        {message}
      </p>

      {/* Time remaining */}
      <p className="text-xs text-muted-foreground dark:text-muted-foreground">
        {progress >= 100
          ? 'Completing...'
          : `~${remaining}s remaining`}
      </p>

      {/* Cancel button */}
      <Button variant="outline" size="sm" onClick={onCancel}>
        <X className="h-4 w-4 mr-1" />
        Cancel Scan
      </Button>
    </div>
  );
}
